import React, { useState, useEffect } from 'react';
import { Button, Icon, Input, Table, message } from 'antd';

/*
 * tableKit — shared formatting, CSV and antd-v3 table helpers for AssetPro reports.
 * ------------------------------------------------------------------------------
 * These lived as module-private consts inside FuelStockIssueReport.js (and, in the
 * case of escapeCsv/downloadCsv, were copy-pasted verbatim into three other
 * reports). They were extracted here so the Register Reconciliation files reuse
 * them instead of adding a fifth copy.
 *
 * Everything is byte-identical to the original EXCEPT ExportableTable, which
 * gained three optional pass-through props — expandedRowRender, rowClassName and
 * onRow. They are forwarded straight to antd's Table and are undefined unless a
 * caller supplies them, so every pre-existing call site behaves exactly as
 * before.
 *
 * DATA RELIABILITY (see the global AssetPro rules)
 *   - fmt* render a non-finite/absent value as an em dash, never as 0.
 *   - toNum maps ''/null/undefined/'--' to null (MISSING), never to 0. A stored
 *     0 is a true zero and stays 0.
 *   - downloadCsv writes an empty cell only for null/undefined; a real 0 is
 *     preserved.
 */

// --- formatting -------------------------------------------------------------
export const fmtL = (v) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

export const fmtMoney = (v) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    : '—';

// Meter readings can carry fractional values (e.g. 15705.1) — show up to 3 dp.
export const fmtMeter = (v) =>
  typeof v === 'number' && Number.isFinite(v)
    ? v.toLocaleString('en-IN', { maximumFractionDigits: 3 })
    : '—';

// Treat null/undefined/''/'--' as missing (null). Anything else -> finite number or null.
export const toNum = (v) => {
  if (v === null || v === undefined || v === '' || v === '--') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

// --- CSV --------------------------------------------------------------------
export const escapeCsv = (v) => {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

export const downloadCsv = (filename, header, rows) => {
  const lines = [header.map(escapeCsv).join(',')];
  rows.forEach((r) => lines.push(r.map(escapeCsv).join(',')));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

// Blank only null/undefined for a CSV cell — a true 0 must survive the export.
export const nzCsv = (v) => (v === null || v === undefined ? '' : v);

// --- Generic table helpers (sort / filter / export) ------------------------
export const strSorter = (key) => (a, b) =>
  String(a[key] == null ? '' : a[key]).localeCompare(String(b[key] == null ? '' : b[key]));

export const numSorter = (key) => (a, b) => (Number(a[key]) || 0) - (Number(b[key]) || 0);

// A per-column text-search filter (works on any field via an optional getText).
export const searchProps = (dataIndex, getText) => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <Input
        placeholder="Search"
        value={selectedKeys[0]}
        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
        onPressEnter={() => confirm()}
        style={{ width: 180, marginBottom: 8, display: 'block' }}
      />
      <Button type="primary" size="small" icon="search" style={{ width: 86, marginRight: 8 }} onClick={() => confirm()}>Search</Button>
      <Button size="small" style={{ width: 86 }} onClick={() => { clearFilters(); confirm(); }}>Reset</Button>
    </div>
  ),
  filterIcon: (filtered) => <Icon type="search" style={{ color: filtered ? '#1890ff' : undefined }} />,
  onFilter: (value, record) => {
    const t = getText ? getText(record) : record[dataIndex];
    return String(t == null ? '' : t).toLowerCase().includes(String(value).toLowerCase());
  },
});

// A categorical (checkbox) filter for low-cardinality fields. String-coerced so
// boolean filter values (Yes/No) match regardless of antd's internal key typing.
export const enumFilter = (key, options) => ({
  filters: options.map((o) => ({ text: o.text, value: o.value })),
  onFilter: (val, record) => String(record[key]) === String(val),
});

// Wraps an antd Table with a "Download CSV" button that exports exactly what is
// currently shown — i.e. after the table's own column filters/sorting are
// applied (captured from onChange's currentDataSource).
export function ExportableTable({ columns, dataSource, rowKey, size, scroll, loading, pagination, exportName, exportHeader, exportRow, expandedRowRender, rowClassName, onRow }) {
  const [view, setView] = useState(dataSource);
  useEffect(() => { setView(dataSource); }, [dataSource]);
  const rows = view && view.length ? view : dataSource;
  const doExport = () => {
    if (!rows.length) {
      message.warning('Nothing to export.');
      return;
    }
    downloadCsv(exportName, exportHeader, rows.map(exportRow));
  };
  return (
    <div>
      <div style={{ marginBottom: 8, textAlign: 'right' }}>
        <Button size="small" icon="download" disabled={!dataSource.length} onClick={doExport}>
          Download CSV ({rows.length})
        </Button>
      </div>
      <Table
        columns={columns}
        dataSource={dataSource}
        rowKey={rowKey}
        size={size}
        scroll={scroll}
        loading={loading}
        pagination={pagination}
        expandedRowRender={expandedRowRender}
        rowClassName={rowClassName}
        onRow={onRow}
        onChange={(p, f, s, extra) => setView(extra && extra.currentDataSource ? extra.currentDataSource : dataSource)}
      />
    </div>
  );
}
